import { Failure, Success } from "@pdfking/errors"
import { NEG_INFINITY, POS_INFINITY } from "@pdfking/math"
import { ToArray } from "@pdfking/tfunc"

import type { MaxError, MaxLengthError, MinError, MinLengthError, ValidationResult } from "#public"

type Num = number | bigint
type Bounds = { min?: Num; max?: Num }

// Shared core: confirm every value (one or a list) sits within [min, max],
// failing with the given min/max error codes on the first out-of-range value.
// Open-ended when a bound is undefined; works for number and bigint alike.
const check = (
  values: Num | Num[],
  { min, max }: Bounds,
  input: unknown,
  minError: string,
  maxError: string
) => {
  for (const value of ToArray(values)) {
    if (value < (min ?? NEG_INFINITY)) return Failure(minError, { value, min, input })
    if (value > (max ?? POS_INFINITY)) return Failure(maxError, { value, max, input })
  }
  return Success(values)
}

// Bound a number (or each number in a list): MIN_ERROR/MAX_ERROR.
export function CheckRange<T extends Num>(
  values: T,
  bounds: Bounds,
  input: unknown
): ValidationResult<T, MinError | MaxError>
export function CheckRange<T extends Num>(
  values: T[],
  bounds: Bounds,
  input: unknown
): ValidationResult<T[], MinError | MaxError>
export function CheckRange<T extends Num>(values: T | T[], bounds: Bounds, input: unknown) {
  return check(values, bounds, input, "MIN_ERROR", "MAX_ERROR")
}

// Bound a length/count: same logic, but MIN_LENGTH_ERROR/MAX_LENGTH_ERROR so a
// "too few/many items" message reads differently from a number out of range.
export const CheckLength = (
  length: number,
  bounds: Bounds,
  input: unknown
): ValidationResult<number, MinLengthError | MaxLengthError> =>
  check(length, bounds, input, "MIN_LENGTH_ERROR", "MAX_LENGTH_ERROR") as ValidationResult<
    number,
    MinLengthError | MaxLengthError
  >
